import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Slide1 from './slides/Slide1';
import Slide2 from './slides/Slide2';
import Slide3 from './slides/Slide3';
import Slide4 from './slides/Slide4';
import Slide5 from './slides/Slide5';
import Slide6 from './slides/Slide6';
import Slide7 from './slides/Slide7';
import Slide8 from './slides/Slide10';
import Slide9 from './slides/Slide11';

const PresentationContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%);
  overflow: hidden;
  position: relative;
`;

const SlideWrapper = styled(motion.div)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
`;

const SlideIndicator = styled.div`
  position: fixed;
  right: 2rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 1000;
`;

const IndicatorDot = styled(motion.div)`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.active ? 'linear-gradient(45deg, #6F1A3F, #C82B3F)' : 'rgba(111, 26, 63, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(45deg, #6F1A3F, #C82B3F);
    transform: scale(1.2);
  }
`;

const SlideCounter = styled.div`
  position: fixed;
  top: 1rem;
  right: 2rem;
  color: #ccc;
  font-size: 1.1rem;
  font-weight: 600;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.8);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(111, 26, 63, 0.3);
`;

const FullscreenButton = styled(motion.button)`
  position: fixed;
  top: 1rem;
  left: 2rem;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(111, 26, 63, 0.3);
  color: #C82B3F;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  z-index: 1000;
  backdrop-filter: blur(10px);

  &:hover {
    background: linear-gradient(45deg, #6F1A3F, #C82B3F);
    color: #fff;
  }
`;

const slides = [
  { component: Slide1, title: "Title Slide" },
  { component: Slide2, title: "Company Overview" },
  { component: Slide3, title: "Mission & Vision" },
  { component: Slide4, title: "Core Technology Solutions" },
  { component: Slide5, title: "Digital Marketing & Brand" },
  { component: Slide6, title: "Technology Projects" },
  { component: Slide7, title: "Marketing Projects" },
  { component: Slide8, title: "Why Choose Us" },
  { component: Slide9, title: "Contact Information" }
];

function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragThreshold] = useState(50); // Minimum drag distance to trigger slide change

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Mouse wheel scroll handler
  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY > 0) {
      nextSlide();
    } else {
      prevSlide();
    }
  };

  // Touch/Mouse drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
  };

  const handleMouseUp = (e) => {
    if (!isDragging) return;
    
    const dragEnd = e.clientX;
    const dragDistance = dragEnd - dragStart;
    
    if (Math.abs(dragDistance) > dragThreshold) {
      if (dragDistance > 0) {
        prevSlide();
      } else {
        nextSlide();
      }
    }
    
    setIsDragging(false);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setDragStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
  };

  const handleTouchEnd = (e) => {
    if (!isDragging) return;
    
    const dragEnd = e.changedTouches[0].clientX;
    const dragDistance = dragEnd - dragStart;
    
    if (Math.abs(dragDistance) > dragThreshold) {
      if (dragDistance > 0) {
        prevSlide();
      } else {
        nextSlide();
      }
    }
    
    setIsDragging(false);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          prevSlide();
          break;
        case 'Escape':
          if (document.fullscreenElement) {
            document.exitFullscreen();
            setIsFullscreen(false);
          }
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        default:
          break;
      }
    };

    // Add wheel event listener
    const container = document.getElementById('presentation-container');
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  const CurrentSlideComponent = slides[currentSlide].component;

  return (
    <PresentationContainer
      id="presentation-container"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => setIsDragging(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      <AnimatePresence mode="wait">
        <SlideWrapper
          key={currentSlide}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5 }}
        >
          <CurrentSlideComponent />
        </SlideWrapper>
      </AnimatePresence>

      <SlideIndicator>
        {slides.map((_, index) => (
          <IndicatorDot
            key={index}
            active={currentSlide === index}
            onClick={() => goToSlide(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </SlideIndicator>

      <SlideCounter>
        {currentSlide + 1} / {slides.length}
      </SlideCounter>

      <FullscreenButton
        onClick={toggleFullscreen}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen (F)'}
      </FullscreenButton>
    </PresentationContainer>
  );
}

export default App;